import { ArchiveIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateWorkspaceDialog } from '../../features/workspace/create-workspace';
import { cn } from '../../shared/lib/cn';
import { type ShellSlotContext, workspacePath } from './nav-config';
import { sidebarActionClass as actionClass, RailTooltip } from './sidebar';

/** Under the workspace list: the archived toggle (when there are any) and "New workspace" (workspaces.manage). */
export function WorkspaceNavFooter({
  collapsed,
  onNavigate,
  org,
  archivedCount,
  showArchived,
  onToggleArchived,
  canCreate,
}: ShellSlotContext & {
  org: { id: string; slug: string };
  archivedCount: number;
  showArchived: boolean;
  onToggleArchived: () => void;
  canCreate: boolean;
}) {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const archivedLabel = showArchived ? t('nav.hideArchived') : t('nav.showArchived');
  return (
    <>
      {archivedCount > 0 ? (
        <RailTooltip show={collapsed} label={archivedLabel}>
          {/* design-system-allow: sidebar action styled like a nav item, not a Button variant */}
          <button
            type="button"
            aria-expanded={showArchived}
            {...(collapsed ? { 'aria-label': archivedLabel } : {})}
            onClick={onToggleArchived}
            className={cn(actionClass, collapsed && 'w-10 justify-center px-0')}
          >
            <ArchiveIcon aria-hidden="true" className="size-5 shrink-0" />
            {collapsed ? null : <span className="truncate">{archivedLabel}</span>}
          </button>
        </RailTooltip>
      ) : null}
      {canCreate ? (
        <RailTooltip show={collapsed} label={t('nav.new')}>
          {/* design-system-allow: sidebar action styled like a nav item, not a Button variant */}
          <button
            type="button"
            {...(collapsed ? { 'aria-label': t('nav.new') } : {})}
            onClick={() => setCreating(true)}
            className={cn(actionClass, collapsed && 'w-10 justify-center px-0')}
          >
            <PlusIcon aria-hidden="true" className="size-5 shrink-0" />
            {collapsed ? null : <span className="truncate">{t('nav.new')}</span>}
          </button>
        </RailTooltip>
      ) : null}
      <CreateWorkspaceDialog
        org={org}
        open={creating}
        onOpenChange={setCreating}
        onCreated={(workspace) => {
          onNavigate?.();
          navigate(workspacePath(org.slug, workspace.slug));
        }}
      />
    </>
  );
}
